import {Injectable, OnDestroy} from '@angular/core';
import {combineLatest, Subject} from 'rxjs';
import {Conclusion} from '@namespaces/conclusion.namespace';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {TechnicalDeviceNamespace} from '@namespaces/technical-device.namespace';
import {ActivatedRoute} from '@angular/router';
import {FormHelperService} from '@services/form-helper.service';
import {catchError, distinctUntilChanged, filter, map, pairwise, startWith, takeUntil} from 'rxjs/operators';
import {untilDestroyed} from 'ngx-take-until-destroy';
import {SelectValueInterface} from '@interfaces/select-value.interface';
import {DynamicFieldsHelperService} from '@subforms/dynamic-fields/dynamic-fields-helper.service';
import {ErrorLoggerService} from '@services/error-logger.service';
import {DynamicFields} from '@namespaces/dynamic-fields.namespace';
import {ConclusionApiService} from '@api/conclusion.api.service';
import {BehaviorSubject} from 'rxjs';
import SignerStructure = Conclusion.SignerStructure;
import SignerSelectValue = Conclusion.SignerSelectValue;
import SignerFormValue = Conclusion.SignerFormValue;
import SignerFormStructure = Conclusion.SignerFormStructure;
import Defect = Conclusion.Defect;
import Field = DynamicFields.Field;
import FieldType = DynamicFields.FieldType;
import StatusesApiData = Conclusion.StatusesApiData;
import {of} from 'rxjs';
import {EventsService} from '@services/events.service';

/**
 * Сервис c данными и формой для создания заключений
 */
@Injectable()
export class ConclusionFormDataService implements OnDestroy {
    /**
     * Ids заключения, объекта, элемента контроля и техкарты
     */
    conclusionId: number;
    objectId: number;
    technicalDeviceId: number;
    technicalCardId: number;

    technicalDeviceElement: TechnicalDeviceNamespace.Element;
    technicalCard: TechnicalDeviceNamespace.ElementGroupTechcardInterface;

    /**
     * Доступные специалисты, в качестве подписантов.
     */
    specialists: SignerSelectValue[] = [];

    /**
     * Структура полей подписантов
     */
    signersStructure: SignerStructure[] = [];

    /**
     * Структура полей подписантов с созданной для каждого подписанта формой
     */
    signersFormStructure: SignerFormStructure[] = [];

    /**
     * Список испытаний
     */
    inspectionsList$ = new BehaviorSubject<Conclusion.InspectionWithForm[]>([]);

    /**
     * Список испытаний обновляется прямо сейчас?
     */
    inspectionsUpdating$ = new BehaviorSubject<boolean>(true);

    /**
     * Доступные статусы для испытаний и заключения
     */
    statuses$ = new BehaviorSubject<StatusesApiData>(null);

    form: FormGroup = this.fb.group({
        id: [],
        equipment_ids: [],
        signers: this.fb.array([]),
        attestation_document_id: [],
        number: [],
        date: [],
        reason: [Conclusion.ConclusionReason.primarily],
        inspections: this.fb.array([]),
        defects_total_length: [0],
        conclusion_status_id: [],
        with_inspections: [false],
        is_generate_conclusion: [false],
    });

    private _isInspectionSelected$ = new BehaviorSubject<boolean>(false);
    public isInspectionSelected$ = this._isInspectionSelected$.asObservable();

    private preventStatusesUpdate$ = new Subject();

    private destroyed$ = new Subject();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private logger: ErrorLoggerService,
        private api: ConclusionApiService,
        private events: EventsService,
    ) {

    }

    init() {
        /**
         * Забираем все нужные значения из параметров и данных роутинга
         */
        const conclusionView: Conclusion.ViewData = this.route.snapshot.data.conclusionView;
        const conclusionFormData: Conclusion.FormData = this.route.snapshot.data.conclusion;

        this.conclusionId = conclusionView.id;
        this.technicalDeviceElement = conclusionView.technical_device_element;
        this.objectId = conclusionView.working_object.id;
        this.technicalDeviceId = conclusionView.technical_device.id;
        this.technicalCardId = conclusionView.technical_card.id;
        this.technicalCard = conclusionView.technical_card;

        this.specialists = this.route.snapshot.data.signersSpecialistsList || [];
        this.signersStructure = this.route.snapshot.data.signersStructure || [];

        FormHelperService.setFormValues(conclusionFormData, this.form);

        this.signersFormStructure = this.createSignersFormStructure();

        /**
         * Обнуляем контрол с оборудованием, так как его значение будет перезаписано
         * и контрол должен иметь значение с типом number[]
         */
        this.form.setControl('equipment_ids', this.fb.control([]));

        this.form.get('with_inspections').valueChanges.pipe(
            startWith<boolean, boolean>(this.form.get('with_inspections').value),
            takeUntil(this.destroyed$),
        ).subscribe((isSelected) => this._isInspectionSelected$.next(isSelected));

        this.isInspectionSelected$.pipe(
            pairwise(),
            filter(([prev, current]) => {
                return prev && !current && this.inspectionsList$.getValue().length > 0;
            }),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.events.showConfirm({
                title: 'Вы уверены, что хотите изменить тип?',
                message: 'Данные результатов контроля будут удалены после сохранения'
            }).subscribe(() => {}, () => {
                this.form.get('with_inspections').patchValue(true);
            });
        });

        /**
         * Для нового заключения всегда выбрано "Не указывать результаты контроля"
         */
        if (!conclusionFormData.conclusion_status_id) {
            this.form.get('with_inspections').patchValue(false);
        }

        this.updateInspectionForm();
    }

    updateEquipment(ids: number[] = []) {
        FormHelperService.updateWithTouch(this.form.get('equipment_ids'), ids);
    }

    /**
     * Обновить кусочек формы, ответственной за испытания
     * @param resetTotalLength - Сбрасывать ли значение суммарной протяженности дефектов
     */
    updateInspectionForm(resetTotalLength = false) {
        this.inspectionsUpdating$.next(true);

        /**
         * Для испытаний нам нужно получить много параметров
         */
        combineLatest([
            this.api.get({
                url: this.api.getInspectionParamsPath(this.conclusionId)
            }),
            this.api.get({
                url: this.api.getInspectionsPath(this.conclusionId)
            }),
            this.api.get({
                url: this.api.getDefectParamsPath(this.conclusionId)
            }),
            this.api.get({
                url: this.api.getStatusesPath(this.conclusionId)
            }).pipe(
                catchError(() => of(null))
            ),
            ]
        ).pipe(
            untilDestroyed(this),
        ).subscribe((value: [Field[], Conclusion.InspectionFormValue[], Field[], StatusesApiData]) => {
            this.initInspectionForm(value[0], value[1], value[2]);

            this.statuses$.next(value[3]);

            if (resetTotalLength) {
                FormHelperService.updateWithTouch(this.form.get('defects_total_length'), 0);
            }
            this.inspectionsUpdating$.next(false);
        }, () => {
            this.inspectionsList$.next([]);
            this.inspectionsUpdating$.next(false);
        });
    }

    updateStatuses() {
        this.preventStatusesUpdate$.next();

        this.api.get({
            url: this.api.getStatusesPath(this.conclusionId)
        }).pipe(
            takeUntil(this.preventStatusesUpdate$),
            untilDestroyed(this),
        ).subscribe((statuses: StatusesApiData) => {
            this.statuses$.next(statuses);
        }, () => {
            this.statuses$.next(null);
        });
    }

    /**
     * Инифицализировать форму для испытаний.
     *
     * Нужно взять текущее значение формы и эталонную структуру формы,
     * затем создать комбинированную структуру данных для удобного рендеринга и управления формой
     * и потом ссылочно прикрепить вложенные формы к базовой форме
     * @param params
     * @param inspections
     * @param defectParams
     */
    private initInspectionForm(
        params: DynamicFields.Field[],
        inspections: Conclusion.InspectionFormValue[],
        defectParams: Field[],
    ) {

        if (this.form.get('is_generate_conclusion').value === false) {
            return;
        }

        try {
            const withForm: Conclusion.InspectionWithForm[] = inspections.map(item => {

                /**
                 * Инициализируем динамические параметры испытаний на основе конфигураций и имеющихся значений
                 */
                const paramsWithForm: DynamicFields.ParamWithForm[] = DynamicFieldsHelperService.createParamsWithForm(
                    params,
                    item && item.params || []
                );

                /**
                 * Забираем дефекты из репрезентера испытаний
                 */
                const defects: Defect[] = item.defects || [];

                return {
                    id: item && item.id || null,
                    paramsWithForm: paramsWithForm,
                    /**
                     * Отображаем только не скрытые поля определенного типа
                     */
                    showedParamsWithForm: paramsWithForm.filter(p => !p.hidden && p.input_type !== FieldType.hidden),
                    defectParams: defectParams,
                    defects: defects,
                    fbGroup: this.fb.group({
                        id: [item && item.id || null],
                        inspection_status_id: [item.inspection_status_id || ''],
                        params: this.fb.array(paramsWithForm.map(p => p.fbGroup))
                    })
                };
            });

            this.inspectionsList$.next(withForm);

            const inspectionsArray: FormArray = this.form.get('inspections') as FormArray;

            FormHelperService.clearArray(inspectionsArray);
            withForm.map(item => item.fbGroup).forEach(group => {
                inspectionsArray.push(group);
            });

            FormHelperService.updateWithTouch(inspectionsArray);
        } catch (e) {
            console.error('Unable to initInspectionForm', e);
            this.logger.log(e, 'ConclusionFormDataService.initInspectionForm');
        }
    }

    /**
     * Сосздать структуру формы подписантов для рендеринга во View
     * @param structure
     */
    private createSignersFormStructure(structure: SignerStructure[] = this.signersStructure): SignerFormStructure[] {
        const signersArray = this.form.get('signers') as FormArray;
        const currentValue: SignerFormValue[] = signersArray.value;

        FormHelperService.clearArray(signersArray);

        /**
         * Добавляем к параметрам подписантов форму, в которой будут релать значения
         */
        const formStructure: SignerFormStructure[] = structure.map((struc) => {
            const fbGroup = this.createSignerFormGroup(struc.id);

            const currentGroupValue = currentValue.find(value => Number(value.type_id) === Number(struc.id));

            if (currentGroupValue) {
                /**
                 * В форме находятся дополнительные поля
                 * id и conclusion_id со значениями, поэтому через setFormValues
                 * мы смержим их с новой структурой
                 */
                FormHelperService.setFormValues(currentGroupValue, fbGroup);
            }

            return {
                ...struc,
                fbGroup,
                certificates_values: [],
            };
        });

        formStructure.forEach(struc => {
            /**
             * Значения каждой формы подписанта прокидываем в результирующую форму
             */
            signersArray.push(struc.fbGroup);

            /**
             * При изменнении выбранного специалиста меняем значения его удостоверений для выбора
             */
            struc.fbGroup.get('specialist_id').valueChanges.pipe(
                distinctUntilChanged(),
                // skip(1),
                map(id => {
                    return this.getCertificatedBySpecialistId(id, struc.certificate_levels || []);
                }),
                untilDestroyed(this)
            ).subscribe((certificates: SelectValueInterface[]) => {
                struc.certificates_values = certificates;
                FormHelperService.updateWithTouch(struc.fbGroup.get('certificate_method_id'));

                if (!certificates.length) {
                    struc.fbGroup.get('certificate_method_id').disable();
                } else {
                    struc.fbGroup.get('certificate_method_id').enable();
                }
            });

            struc.fbGroup.get('specialist_id').updateValueAndValidity();
        });

        return formStructure;
    }

    /**
     * Получить удостоверения специалиста
     * @param id
     * @param levels
     */
    private getCertificatedBySpecialistId(id: number|string, levels: number[] = []): SelectValueInterface[] {
        const specialist = this.specialists.find(s => Number(s.id) === Number(id));
        const certificates = specialist && specialist.certificates || [];
        return certificates.filter(c => levels.includes(c.level));
    }

    /**
     * Создать новую группу полей для подписанта
     * @param type_id
     */
    private createSignerFormGroup(type_id: number): FormGroup {
        return this.fb.group({
            type_id: [type_id],
            date: [],
            specialist_id: [''],
            certificate_method_id: [''],
            outside_specialist: [''],
            outside_certificate: [''],
        });
    }

    ngOnDestroy() {
        this.inspectionsList$.complete();
        this.statuses$.complete();
        this.preventStatusesUpdate$.complete();
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
